import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, Sparkles, Crown, Globe, ArrowLeft } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { detectCurrency, setCurrency as persistCurrency, formatPrice } from "../lib/currency";

const FEATURES = {
  free: [
    "100 MB storage",
    "Unlimited words & categories",
    "Photo-to-vocab AI scans",
    "AI definition suggestions",
    "Documents & images uploads",
    "1 AI quiz generation per day",
  ],
  pro_standard: [
    "Up to 4 GB storage",
    "Audio & video uploads",
    "Unlimited AI quiz generations",
    "Everything in Free",
    "Priority support",
  ],
  pro_plus: [
    "Up to 8 GB storage",
    "Audio & video uploads",
    "Slides → Notes (PDF & PPTX lecture deck summariser)",
    "AI assignment draft assistant",
    "Unlimited AI quiz generations",
    "Everything in Pro",
    "Priority support",
  ],
};

function PlanCard({ id, label, priceLabel, hasPrice, highlighted, current, onChoose, busy, color, signedIn }) {
  let label_btn = "Choose";
  if (id === "free") label_btn = "Default";
  else if (current) label_btn = "Renew · extend 30 days";
  else if (!signedIn) label_btn = "Sign in to choose";
  const features = FEATURES[id] || [];
  return (
    <div className="nb-card p-6" style={{ background: color }}>
      <div className="flex items-center gap-2">
        {highlighted && <Crown className="w-5 h-5" strokeWidth={2.5}/>}
        <h3 className="font-display text-2xl font-black">{label}</h3>
        {current && <span className="nb-tag bg-white text-xs">Current</span>}
      </div>
      <div className="mt-4">
        <span className="font-display text-5xl font-black">{priceLabel}</span>
        {hasPrice && <span className="text-sm font-bold text-slate-700">/month</span>}
      </div>
      <ul className="mt-5 space-y-2">
        {features.map((f) => (
          <li key={f} className="flex gap-2 items-start text-sm font-medium">
            <Check className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={3}/>
            <span>{f}</span>
          </li>
        ))}
      </ul>
      {id === "free" ? (
        <div className="mt-6 nb-btn-secondary w-full text-center cursor-default opacity-60">{label_btn}</div>
      ) : (
        <button onClick={() => onChoose(id)} disabled={busy} data-testid={`pricing-${id}-btn`} className="nb-btn-primary mt-6 w-full inline-flex items-center justify-center gap-2">
          <Sparkles className="w-4 h-4" strokeWidth={2.5}/> {busy ? "Loading…" : label_btn}
        </button>
      )}
    </div>
  );
}

export default function Pricing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState({ plans: [], supported_currencies: [], default_currency: "GBP" });
  const [currency, setCurrencyState] = useState(() => detectCurrency());
  const [busy, setBusy] = useState(false);
  const [checkout, setCheckout] = useState(null); // { url, plan }
  const [autoRenew, setAutoRenew] = useState(true); // default to auto-renew (recommended)
  const checkoutLinkRef = useRef(null);

  useEffect(() => {
    api.get("/plans").then((r) => {
      const d = r.data || {};
      // Tolerate old API shape (array of plans) just in case
      if (Array.isArray(d)) {
        setData({ plans: d, supported_currencies: ["GBP"], default_currency: "GBP" });
        return;
      }
      setData({
        plans: Array.isArray(d.plans) ? d.plans : [],
        supported_currencies: Array.isArray(d.supported_currencies) ? d.supported_currencies : ["GBP"],
        default_currency: d.default_currency || "GBP",
      });
      if (Array.isArray(d.supported_currencies) && !d.supported_currencies.includes(currency)) {
        setCurrencyState(d.default_currency || "GBP");
      }
    }).catch(() => {
      toast.error("Could not load plans — please refresh.");
    });
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  const onCurrencyChange = (next) => {
    setCurrencyState(next);
    persistCurrency(next);
  };

  const priceFor = (plan) => {
    const amount = plan?.prices?.[currency] ?? plan?.prices?.GBP ?? 0;
    return { amount, label: amount > 0 ? formatPrice(amount, currency) : formatPrice(0, currency) };
  };

  const choose = async (planId) => {
    if (!user) {
      // Send them to login, return to /pricing afterwards
      navigate("/login?next=/pricing");
      return;
    }
    setBusy(true);
    setCheckout(null);
    try {
      const origin_url = window.location.origin;
      const r = await api.post("/billing/checkout", { plan: planId, origin_url, currency, auto_renew: autoRenew });
      setCheckout({ url: r.data.url, plan: planId });
      // After render, programmatically click the hidden anchor — works in webviews where
      // window.location.assign / href fail silently. Real <a> click survives Chrome Custom Tabs.
      requestAnimationFrame(() => {
        if (checkoutLinkRef.current) {
          checkoutLinkRef.current.click();
        }
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
    } finally {
      setBusy(false);
    }
  };

  const colors = { free: "#FFFFFF", pro_standard: "#FDE047", pro_plus: "#86EFAC" };

  return (
    <div className="p-6 sm:p-8 lg:p-12 max-w-6xl mx-auto">
      {user ? (
        <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm font-bold mb-6 hover:underline" data-testid="pricing-back-dashboard">
          <ArrowLeft className="w-4 h-4" strokeWidth={2.5}/> Back to dashboard
        </Link>
      ) : (
        <Link to="/" className="inline-flex items-center gap-2 text-sm font-bold mb-6 hover:underline" data-testid="pricing-back-home">
          <ArrowLeft className="w-4 h-4" strokeWidth={2.5}/> Back to home
        </Link>
      )}
      <header className="mb-8 text-center">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Plans</p>
        <h1 className="font-display text-4xl sm:text-5xl font-black mt-2">Pick your study sidekick</h1>
        <p className="text-base font-medium text-slate-600 mt-3 max-w-2xl mx-auto">Start free. Upgrade when you need more space, audio/video, or the AI assignment draft helper.</p>

        <div className="mt-5 inline-flex items-center gap-2 nb-card-sm px-3 py-2" data-testid="currency-picker">
          <Globe className="w-4 h-4" strokeWidth={2.5}/>
          <label htmlFor="currency-select" className="text-xs font-bold uppercase tracking-widest">Currency</label>
          <select
            id="currency-select"
            data-testid="currency-select"
            value={currency}
            onChange={(e) => onCurrencyChange(e.target.value)}
            className="bg-transparent font-bold text-sm focus:outline-none cursor-pointer"
          >
            {(data.supported_currencies || ["GBP"]).map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <p className="text-xs text-slate-500 mt-2" data-testid="currency-detected-note">Currency auto-detected from your location — switch above if you'd prefer a different one.</p>

        <label className="mt-5 inline-flex items-start gap-3 cursor-pointer select-none" data-testid="auto-renew-toggle">
          <input
            type="checkbox"
            checked={autoRenew}
            onChange={(e) => setAutoRenew(e.target.checked)}
            data-testid="auto-renew-checkbox"
            className="w-5 h-5 mt-0.5 cursor-pointer accent-yellow-400"
          />
          <span className="text-sm font-bold text-slate-800 leading-snug">
            Auto-renew monthly — cancel anytime
            <span className="block text-xs font-medium text-slate-500 mt-0.5">
              {autoRenew
                ? "Your card will be charged at the start of each month. Cancel from Settings any time."
                : "One-off payment for 30 days. You'll need to renew manually after that."}
            </span>
          </span>
        </label>
      </header>

      {checkout && (
        <div className="nb-card p-5 mb-6 text-center" style={{ background: "#86EFAC" }} data-testid="checkout-fallback">
          <p className="font-display text-lg font-bold">Continue to secure payment</p>
          <a
            ref={checkoutLinkRef}
            href={checkout.url}
            target="_self"
            rel="noopener noreferrer"
            data-testid="checkout-fallback-link"
            className="nb-btn-primary inline-flex items-center gap-2 mt-3"
          >
            Continue → checkout.stripe.com
          </a>
          <p className="text-xs font-medium text-slate-700 mt-3">
            We never see your card details.
          </p>
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-6">
        {(data.plans || []).map((p) => {
          const { amount, label } = priceFor(p);
          return (
            <PlanCard
              key={p.id}
              id={p.id}
              label={p.label}
              priceLabel={label}
              hasPrice={amount > 0}
              highlighted={p.id === "pro_plus"}
              current={user?.plan === p.id}
              onChoose={choose}
              busy={busy}
              color={colors[p.id]}
              signedIn={!!user}
            />
          );
        })}
      </div>

      {!user && (
        <div className="nb-card p-5 mt-8 text-center" style={{ background: "#FDE04733" }}>
          <p className="text-sm font-medium text-slate-800">
            New to Studict? <Link to="/login" className="font-bold underline">Sign in with Google</Link> to start your free plan.
          </p>
        </div>
      )}

      <div className="nb-card p-5 mt-8" style={{ background: "#FDE04733" }}>
        <p className="text-sm font-medium text-slate-800">
          <strong>AI disclaimer:</strong> The AI assignment draft assistant is meant for brainstorming and starting points only.
          AI can make mistakes, miss nuance, or invent facts. Always verify and rewrite in your own voice before submitting.
        </p>
      </div>
    </div>
  );
}
